import type { ComponentType } from "react";

import { useMemo } from "react";

import type { Citation, File } from "@alaffia-technology-solutions/schema";

import { apiClient } from "@alaffia-technology-solutions/client-sdk-react-query";
import {
  badgeVariants,
  MarkdownEditor,
} from "@alaffia-technology-solutions/ds";
import { cn } from "@alaffia-technology-solutions/tailwind-utils";

import type { Operation as JsonPatchOperation } from "fast-json-patch";

import type { DeepLinkData, FilePreviewDeepLinkData } from "../../utils";
import type { ParsedTaskArtifactOutput } from "../utils";

import {
  createDeepLinkDataFromApiInlineCitations,
  createDeepLinkDataFromMessageCitations,
} from "../../utils";

interface CitationProps {
  annotations?: { height: number; left: number; top: number; width: number }[];
  fileId: File["id"];
  page: number;
}

interface RenderMarkdownProps {
  deepLinkData: DeepLinkData;
  markdown: string;
  onDeepLinkClick?: (data: FilePreviewDeepLinkData) => void;
}

interface MessageContentProps {
  taskArtifactOutput: ParsedTaskArtifactOutput;
  fallbackMarkdownContent?: string | null;
  editMode?: boolean;
  onCitationClick?: (props: CitationProps) => void;
  onDeepLinkClick?: (data: FilePreviewDeepLinkData) => void;
  onEdit: (patchOperations: JsonPatchOperation[]) => void;
  renderMarkdown?: ComponentType<RenderMarkdownProps>;
}

export const MessageContent = ({
  taskArtifactOutput,
  fallbackMarkdownContent,
  editMode = false,
  onCitationClick,
  onDeepLinkClick,
  onEdit,
  renderMarkdown,
}: MessageContentProps) => {
  const deepLinkData = useMemo(() => {
    const inlineCitations = taskArtifactOutput?.inline_citations.content ?? [];
    const legacyCitations = Object.values(
      taskArtifactOutput?.citations.content ?? {},
    ).flat();

    return {
      ...createDeepLinkDataFromMessageCitations(legacyCitations),
      ...createDeepLinkDataFromApiInlineCitations(inlineCitations),
    };
  }, [taskArtifactOutput]);

  if (!taskArtifactOutput) {
    if (fallbackMarkdownContent) {
      return (
        <MarkdownContent editable={false} content={fallbackMarkdownContent} />
      );
    }
    return null;
  }

  const { markdown, citations } = taskArtifactOutput;

  return (
    <div>
      <MarkdownContent
        editable={editMode && markdown.editable}
        content={markdown.content}
        deepLinkData={deepLinkData}
        onDeepLinkClick={onDeepLinkClick}
        onEdit={(editedMarkdown) =>
          onEdit([
            {
              op: "replace",
              path: markdown.propertyPath,
              value: editedMarkdown,
            },
          ])
        }
        renderMarkdown={renderMarkdown}
      />
      <CitationsContent
        citations={citations.content}
        onCitationClick={onCitationClick}
      />
    </div>
  );
};

interface MarkdownContentProps {
  content: string;
  deepLinkData?: DeepLinkData;
  editable: boolean;
  onDeepLinkClick?: (data: FilePreviewDeepLinkData) => void;
  onEdit?: (edit: string) => void;
  renderMarkdown?: ComponentType<RenderMarkdownProps>;
}

const MarkdownContent = ({
  content,
  deepLinkData,
  editable,
  onDeepLinkClick,
  onEdit,
  renderMarkdown: RenderMarkdown,
}: MarkdownContentProps) => {
  return (
    <div>
      <div className="af-mb-2 af-text-sm af-text-muted-foreground">
        Conclusion:
      </div>
      {!editable && RenderMarkdown && deepLinkData ? (
        <RenderMarkdown
          deepLinkData={deepLinkData}
          markdown={content}
          onDeepLinkClick={onDeepLinkClick}
        />
      ) : (
        <MarkdownEditor
          key={`${content}-${editable}`}
          initialMarkdownContent={content}
          editable={editable}
          onChange={onEdit}
        />
      )}
    </div>
  );
};

interface CitationsContentProps {
  citations: Record<File["id"], Citation[]>;
  onCitationClick?: (props: CitationProps) => void;
}

const CitationsContent = ({
  citations,
  onCitationClick,
}: CitationsContentProps) => {
  const citationsEntries = Object.entries(citations);

  if (!citationsEntries.length) return null;

  return (
    <div className="af-mt-4">
      <h3 className="af-mb-4 af-text-sm af-text-muted-foreground">
        Citations:
      </h3>

      <ul className="af-flex af-list-none af-flex-col af-gap-4 af-p-0">
        {citationsEntries.map(([fileId, citations]) => (
          <CitationsGroup
            key={fileId}
            fileId={fileId}
            citations={citations}
            onCitationClick={onCitationClick}
          />
        ))}
      </ul>
    </div>
  );
};

interface CitationsGroupProps {
  fileId: File["id"];
  citations: Citation[];
  onCitationClick?: (props: CitationProps) => void;
}

function CitationsGroup({
  fileId,
  citations,
  onCitationClick,
}: CitationsGroupProps) {
  const { data: fileData } = apiClient.files.getFile.useQuery({ id: fileId });

  return (
    <li>
      <ul className="af-flex af-list-none af-flex-wrap af-gap-2 af-p-0">
        {citations.map((citation, index) => (
          <li key={citation.citation_key + index}>
            <BadgeCitation citation={citation} onClick={onCitationClick} />
          </li>
        ))}
      </ul>

      {fileData && (
        <div className="af-mt-2.5 af-w-full af-text-xs af-text-muted-foreground">
          <p>Source file: {fileData.fileName}</p>
        </div>
      )}
    </li>
  );
}

interface BadgeCitationProps {
  citation: Citation;
  onClick?: (props: CitationProps) => void;
}

function BadgeCitation({ citation, onClick }: BadgeCitationProps) {
  return (
    <button
      className={cn(
        badgeVariants({ variant: "secondary" }),
        "af-bg-secondary/40 af-font-medium af-text-primary hover:af-bg-secondary/90",
      )}
      onClick={() =>
        onClick?.({
          fileId: citation.file_id,
          page: citation.page_index,
        })
      }
    >
      Page {citation.page_index}
    </button>
  );
}
